import React, { useMemo } from 'react';
import { Button, Typography } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import { useErrorMessage } from '@shared/hooks/use-error-message';
import { moveToGroupErrorMessages } from '@modules/digital-signage/utils/digital-signage.utils.ts';
import MoveToGroupAlert from './components/move-to-group-alert/move-to-group-alert.tsx';
import {
  MoveToGroupDialogFormProps,
  MoveToGroupFormValues
} from '@modules/digital-signage/model/devices.model.ts';
import useDeviceStore from '../../store/devices.store.ts';

const MoveToGroupDialogForm: React.FC<MoveToGroupDialogFormProps> = ({
  sourceDevices,
  selectedDevices,
  deviceGroupsData,
  onDevicesChange,
  onSave
}) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading, isOptionsLoading } = useDeviceStore();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<MoveToGroupFormValues>({
    mode: 'onTouched',
    defaultValues: { deviceGroup: null }
  });

  const deviceGroupValue = useWatch({ control, name: 'deviceGroup' });

  const selectedDeviceGroupList = useMemo(
    () => (deviceGroupValue ? [deviceGroupValue] : []),
    [deviceGroupValue]
  );

  const deviceIds = useMemo(
    () =>
      selectedDevices
        .map((device) => device._id)
        .filter((id): id is string => !!id),
    [selectedDevices]
  );

  const onSubmit = (data: MoveToGroupFormValues) => {
    if (!data.deviceGroup || !deviceIds.length) return;

    onSave({
      deviceGroupId: data.deviceGroup.id,
      devices: deviceIds
    });
  };

  return (
    <form className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <MoveToGroupAlert
          sourceDevices={sourceDevices}
          selectedDevices={selectedDevices}
          onChange={onDevicesChange}
        />

        <Typography
          variant="caption"
          component="p"
          className="min-h-4.5 text-(--red)!"
        >
          {!deviceIds.length && t('move_to_group_no_devices_selected')}
        </Typography>
      </div>

      <AppAutocompleteV2
        name="deviceGroup"
        control={control}
        label={
          <span className="flex items-center">
            <span className="reuqired-symbole">*</span>
            {t('label_device_group')}
          </span>
        }
        options={deviceGroupsData}
        selectedList={selectedDeviceGroupList}
        placeholder={t('placeholder_device_group')}
        loading={isOptionsLoading()}
        validationProps={{
          errors,
          errorMessages: moveToGroupErrorMessages,
          getErrorMessage,
          validationRules: { required: true }
        }}
      />

      <div className="flex justify-end pt-2">
        <Button
          variant="contained"
          disabled={!isValid || !deviceIds.length}
          loading={isLoading()}
          onClick={handleSubmit(onSubmit)}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
};

export default MoveToGroupDialogForm;
